import React, { useState, useEffect, useRef } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import { supabase, SUPABASE_ANON_KEY } from '../lib/supabaseClient';
import { useAuth } from '../contexts/auth/AuthContext';
import StripeProvider from '../components/stripe/StripeProvider';
import { edgeFunctionUrl, EdgeFunctions } from '../lib/edgeFunctions';

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
  hidePostalCode: true,
};

// Inner component to use Stripe hooks
const MembershipCheckoutForm = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();

  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const creationInitiatedRef = useRef(false);

  useEffect(() => {
    const createSubscription = async () => {
      // Guard against re-running the effect.
      // A ref is used because it persists across React's development-mode re-mounts.
      if (!user || creationInitiatedRef.current) {
        if (!user) {
          setErrorMessage('You must be logged in to subscribe.');
        }
        return;
      }
      
      // Set the ref to true immediately and synchronously to prevent re-entry.
      creationInitiatedRef.current = true;
      setIsProcessing(true);
      setErrorMessage(null);

      try {
        const { data: { session } } = await supabase.auth.getSession();
        const accessToken = session?.access_token;

        if (!accessToken) {
          throw new Error('Authentication required.');
        }

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        };

        const response = await fetch(
          edgeFunctionUrl(EdgeFunctions.CREATE_SUBSCRIPTION),
          {
            method: 'POST',
            headers,
            body: JSON.stringify({
              email: user.email,
              name: user.user_metadata.full_name || 'New Member',
              user_id: user.id,
            }),
          }
        );

        const responseData = await response.json();

        if (!response.ok) {
          throw new Error(responseData.error || 'Failed to create subscription.');
        }

        if (!responseData.clientSecret) {
          throw new Error('No client secret returned from server.');
        }

        setClientSecret(responseData.clientSecret);
      } catch (error: any) {
        console.error('Error creating subscription:', error);
        setErrorMessage(error.message || 'An unexpected error occurred.');
      } finally {
        setIsProcessing(false);
      }
    };

    createSubscription();
  }, [user]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements || !clientSecret || !user) {
      setErrorMessage('Stripe is not ready, or you are not logged in.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) throw new Error('Card element not found.');

      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: user.user_metadata.full_name || 'New Member',
            email: user.email,
          },
        },
      });

      if (error) {
        throw new Error(error.message || 'Payment failed.');
      }

      if (paymentIntent?.status === 'succeeded') {
        console.log('Subscription payment successful!');
        navigate('/join-success');
      } else {
        throw new Error(`Payment processing. Status: ${paymentIntent?.status}`);
      }
    } catch (err: any) {
      console.error('Payment error:', err);
      setErrorMessage(err.message || 'An unexpected error occurred.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto mt-10 p-8 bg-white shadow-lg rounded-lg">
      <h2 className="text-3xl font-serif text-center text-[#232347] mb-4">Join Our Membership</h2>
      
      <div className="bg-[#F1F7F4] p-6 rounded-md mb-8 text-center">
        <h3 className="text-xl font-semibold text-[#232347]">Monthly Membership</h3>
        <p className="text-4xl font-bold text-[#3A5E63] my-2">$189<span className="text-lg font-normal">/month</span></p>
        <p className="text-gray-600">Enjoy exclusive benefits and unlimited access.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-lg text-[#232347] mb-2">Payment Details</label>
          <div className="border border-gray-300 p-4 rounded-md bg-white">
            <CardElement options={cardElementOptions} />
          </div>
        </div>
        
        {errorMessage && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md">
            {errorMessage}
          </div>
        )}

        {isProcessing && !clientSecret && (
             <div className="flex items-center justify-center text-gray-600">
                <span className="animate-spin h-5 w-5 border-2 border-gray-400 border-t-transparent rounded-full mr-3"></span>
                Preparing your subscription...
            </div>
        )}

        <button
          type="submit"
          disabled={!stripe || !elements || isProcessing || !clientSecret}
          className="w-full px-6 py-3 rounded-md text-white font-semibold bg-[#B2D8C5] hover:bg-[#88bfa3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isProcessing ? (
            <>
              <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-3"></span>
              Processing...
            </>
          ) : (
            'Subscribe Now'
          )}
        </button>

        <p className="text-sm text-gray-500 text-center mt-4">
          By subscribing, you agree to our terms and that your membership will automatically renew monthly. You can cancel anytime.
        </p>
      </form>
    </div>
  );
};


// Main page component that wraps the form with StripeProvider
const JoinPage = () => {
    return (
        <StripeProvider>
            <MembershipCheckoutForm />
        </StripeProvider>
    );
};

export default JoinPage;
